import React, { useState } from 'react';
import { TextField } from '@good/components';
import { Group } from '@good/ui/core';
import { notification, Row } from 'antd';
import ActionModal, { ActionModalFooter } from 'common-components/modal/ActionModal';
import { Paragraph } from 'common-components/typography';
import { Button } from 'design-components';
import { IRootDispatch } from 'stores/rematch/root-store';
import { useDispatch } from 'react-redux';
import { SelectPhoneCountryCode } from 'components/select-phone-country-code/select-phone-country-code';
import { type CountryCode } from 'providers/locale-provider';
import { validatePhoneNumber } from 'utilities/validate-phone-number';
import { formatPhoneNumber, formatPhoneInput } from 'utilities/format-phone-number';

type IEditCompanyPhoneModalProps = {
  isOpen: boolean;
  currentPhone: string;
  currentPhoneCountryCode?: CountryCode;
  onClose: () => void;
};

const validatePhoneWithMessage = (phoneNumber: string, countryCode: CountryCode): string => {
  // Allow empty phone numbers
  if (!phoneNumber.trim()) {
    return '';
  }

  // Use the validatePhoneNumber utility to check validity
  const isValid = validatePhoneNumber(phoneNumber, { countryCode });
  return isValid ? '' : 'Please enter a valid phone number';
};

export const EditCompanyPhoneModal = ({
  onClose,
  isOpen,
  currentPhone,
  currentPhoneCountryCode,
}: IEditCompanyPhoneModalProps) => {
  const { companyStore } = useDispatch<IRootDispatch>();
  const { doEditCompanyPhone } = companyStore;

  const [isLoading, setIsLoading] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [newPhoneCountryCode, setNewPhoneCountryCode] = useState<CountryCode>(currentPhoneCountryCode || 'AU');
  const [errorMessage, setErrorMessage] = useState('');

  const onModalClose = () => {
    setNewPhone('');
    setNewPhoneCountryCode(currentPhoneCountryCode || 'AU');
    setErrorMessage('');
    onClose();
  };

  const onSave = async () => {
    try {
      setIsLoading(true);

      setErrorMessage('');
      const validationErrorMessage = validatePhoneWithMessage(newPhone, newPhoneCountryCode);
      if (validationErrorMessage !== '') {
        setErrorMessage(validationErrorMessage);
        return;
      }

      // Send the clean phone number (digits only) to the backend
      const cleanPhoneForSave = newPhone.replace(/\D/g, '');
      await doEditCompanyPhone({ phone: cleanPhoneForSave, phoneCountryCode: newPhoneCountryCode });
      notification.success({ message: 'Company phone number updated' });
      onModalClose();
    } catch (e) {
      notification.error({ message: 'We were unable to update the company phone number. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onNewPhoneChange = (value: any) => {
    // Format the input as user types
    const formattedValue = formatPhoneInput(value, newPhoneCountryCode);
    setNewPhone(formattedValue);
    // Clear error when user starts typing
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onNewPhoneBlur = (e: any) => {
    // For validation, keep the formatted value as is since libphonenumber-js handles various formats
    setErrorMessage(validatePhoneWithMessage(e.target.value, newPhoneCountryCode));
  };

  const onCountryCodeChange = (countryCode: CountryCode | null) => {
    if (countryCode) {
      setNewPhoneCountryCode(countryCode);
      // Re-validate when country code changes
      if (newPhone) {
        setErrorMessage(validatePhoneWithMessage(newPhone, countryCode));
      }
    }
  };

  return (
    <ActionModal
      title='Change company phone number'
      isOpen={isOpen}
      onClose={onModalClose}
      canCloseOutside={!isLoading}
      width='medium'
    >
      <Paragraph>The company&apos;s phone number will appear on invoices sent to customers.</Paragraph>
      <div className='mt-large'>
        <Row>
          <div className='mb-medium'>
            <TextField
              className='w-full'
              label='Current Phone Number'
              isDisabled
              defaultValue={formatPhoneNumber(currentPhone, currentPhoneCountryCode)}
            />
          </div>

          <div className='mb-medium'>
            <div className='mb-2 text-sm font-medium'>New Phone Number</div>
            <Group wrap='nowrap' gap={4} align='center'>
              <SelectPhoneCountryCode
                value={newPhoneCountryCode}
                onChange={onCountryCodeChange}
                variant='filled'
                style={{ flexShrink: 0, minWidth: '120px' }}
              />
              <TextField
                className='flex-1'
                label=''
                value={newPhone}
                validationState={errorMessage ? 'invalid' : 'valid'}
                errorMessage={errorMessage}
                onChange={onNewPhoneChange}
                onBlur={onNewPhoneBlur}
                placeholder='1300 123 456'
              />
            </Group>
          </div>
        </Row>
      </div>
      <ActionModalFooter>
        <Button marginRight='14px' emphasis='quiet' color='black' onClick={onModalClose} isLoading={isLoading}>
          Cancel
        </Button>
        <Button isDisabled={errorMessage !== ''} emphasis='filled' isLoading={isLoading} onClick={onSave}>
          Save
        </Button>
      </ActionModalFooter>
    </ActionModal>
  );
};
